import { useState, useMemo } from "react";
import {
  useAdminDataPlans,
  useCreateAdminDataPlan,
  useUpdateAdminData,
  useDeleteAdminData,
  useAdminNetworks,
  useAdminApiProviders,
} from "../../../api/admin";
import {
  usePlanTypesList,
  useCreatePlanType,
  useUpdatePlanType,
  useDeletePlanType,
} from "../../../api/data";
import { toast } from "react-hot-toast";

// Backend response types
interface BackendDataPlan {
  id: number;
  network_name: string;
  network: number;
  created_at: string;
  updated_at: string;
  plan_id: number;
  name: string;
  amount: string;
  commission: string | null;
  is_active: boolean;
  validity: string;
  plan_type: number;
  provider: number;
}

interface BackendPlanType {
  id: number;
  name: string;
  is_active: boolean;
  network: number;
}

interface BackendNetwork {
  id: number;
  name: string;
  logo_url: string;
  provider: number;
}

interface BackendProvider {
  id: number;
  name: string;
  is_active: boolean;
}

interface DataPlan {
  id: number;
  network: string;
  networkId: number;
  planId: number;
  name: string;
  amount: number;
  commission: number | null;
  isActive: boolean;
  validity: string;
  planType: number;
  planTypeName: string;
  provider: number;
  providerName: string;
  createdAt: string;
}

interface PlanType {
  id: number;
  name: string;
  isActive: boolean;
  network: number;
  networkName: string;
}

export default function DataPlansManagement() {
  // API Hooks
  const {
    data: dataPlansData,
    isLoading: plansLoading,
    refetch: refetchPlans,
  } = useAdminDataPlans();
  const {
    data: planTypesData,
    isLoading: typesLoading,
    refetch: refetchTypes,
  } = usePlanTypesList();
  const { data: networksData } = useAdminNetworks();
  const { data: providersResponse } = useAdminApiProviders();

  const createPlan = useCreateAdminDataPlan();
  const updatePlan = useUpdateAdminData();
  const deletePlan = useDeleteAdminData();

  const createType = useCreatePlanType();
  const updateType = useUpdatePlanType();
  const deleteType = useDeletePlanType();

  // Local State
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [showTypeModal, setShowTypeModal] = useState(false);
  const [editingPlan, setEditingPlan] = useState<DataPlan | null>(null);
  const [editingType, setEditingType] = useState<PlanType | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterNetwork, setFilterNetwork] = useState("all");
  const [filterType, setFilterType] = useState("all");
  const [activeTab, setActiveTab] = useState<"plans" | "types">("plans");

  const [planFormData, setplanFormData] = useState({
    network: "",
    plan_id: "",
    name: "",
    amount: "",
    commission: "",
    validity: "30",
    plan_type: "",
    provider: "",
    is_active: true,
  });

  const [typeFormData, setTypeFormData] = useState({
    name: "",
    network: "",
    is_active: true,
  });

  // Extract providers from nested response
  const providers = useMemo(() => {
    if (!providersResponse?.results?.data) return [];
    return providersResponse.results.data as BackendProvider[];
  }, [providersResponse]);

  // Map networks
  const networks = useMemo(() => {
    if (!networksData || !Array.isArray(networksData)) return [];
    return networksData as BackendNetwork[];
  }, [networksData]);

  // Map plan types
  const planTypes = useMemo(() => {
    if (!planTypesData || !Array.isArray(planTypesData)) return [];
    return (planTypesData as BackendPlanType[]).map((type) => {
      const network = networks.find((n) => n.id === type.network);
      return {
        id: type.id,
        name: type.name,
        isActive: type.is_active,
        network: type.network,
        networkName: network?.name || `Network ${type.network}`,
      };
    });
  }, [planTypesData, networks]);

  // Map data plans
  const dataPlans = useMemo(() => {
    if (!dataPlansData || !Array.isArray(dataPlansData)) return [];

    return (dataPlansData as BackendDataPlan[]).map((plan) => {
      const planType = planTypes.find((t) => t.id === plan.plan_type);
      const provider = providers.find((p) => p.id === plan.provider);

      return {
        id: plan.id,
        network: plan.network_name,
        networkId: plan.network,
        planId: plan.plan_id,
        name: plan.name,
        amount: parseFloat(plan.amount),
        commission: plan.commission ? parseFloat(plan.commission) : null,
        isActive: plan.is_active,
        validity: plan.validity,
        planType: plan.plan_type,
        planTypeName: planType?.name || "Unknown",
        provider: plan.provider,
        providerName: provider?.name || `Provider ${plan.provider}`,
        createdAt: new Date(plan.created_at).toLocaleDateString(),
      };
    });
  }, [dataPlansData, planTypes, providers]);

  // Filter plans
  const filteredPlans = useMemo(() => {
    return dataPlans.filter((plan) => {
      const matchesSearch =
        plan.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        plan.network.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesNetwork =
        filterNetwork === "all" || plan.network === filterNetwork;
      const matchesType =
        filterType === "all" || plan.planType.toString() === filterType;
      return matchesSearch && matchesNetwork && matchesType;
    });
  }, [dataPlans, searchTerm, filterNetwork, filterType]);

  // Plan CRUD handlers
  const handlePlanSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const payload = {
        network: parseInt(planFormData.network),
        plan_id: parseInt(planFormData.plan_id),
        name: planFormData.name,
        amount: planFormData.amount,
        base_amount:planFormData.amount,
        commission: planFormData.commission || null,
        validity: planFormData.validity,
        plan_type: parseInt(planFormData.plan_type),
        provider: parseInt(planFormData.provider),
        is_active: planFormData.is_active,
      };

      if (editingPlan) {
        await updatePlan.mutateAsync({
          id: editingPlan.id,
          data: payload,
        });
        toast.success("Data plan updated successfully!");
      } else {
        await createPlan.mutateAsync(payload);
        toast.success("Data plan created successfully!");
      }

      refetchPlans();
      resetPlanForm();
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to save data plan";
      toast.error(errorMessage);
      console.error("Error saving plan:", error);
    }
  };

  const handlePlanEdit = (plan: DataPlan) => {
    setEditingPlan(plan);
    setplanFormData({
      network: plan.networkId.toString(),
      plan_id: plan.planId.toString(),
      name: plan.name,
      amount: plan.amount.toString(),
      commission: plan.commission?.toString() || "",
      validity: plan.validity,
      plan_type: plan.planType.toString(),
      provider: plan.provider.toString(),
      is_active: plan.isActive,
    });
    setShowPlanModal(true);
  };

  const handlePlanDelete = async (planId: number) => {
    if (!confirm("Are you sure you want to delete this data plan?")) return;

    try {
      await deletePlan.mutateAsync(planId);
      toast.success("Data plan deleted successfully!");
      refetchPlans();
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to delete data plan";
      toast.error(errorMessage);
      console.error("Error deleting plan:", error);
    }
  };

  const resetPlanForm = () => {
    setplanFormData({
      network: "",
      plan_id: "",
      name: "",
      amount: "",
      commission: "",
      validity: "30",
      plan_type: "",
      provider: "",
      is_active: true,
    });
    setEditingPlan(null);
    setShowPlanModal(false);
  };

  // Plan Type CRUD handlers
  const handleTypeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const payload = {
        name: typeFormData.name,
        network: parseInt(typeFormData.network),
        is_active: typeFormData.is_active,
      };

      if (editingType) {
        await updateType.mutateAsync({
          id: editingType.id,
          data: payload,
        });
        toast.success("Plan type updated successfully!");
      } else {
        await createType.mutateAsync(payload);
        toast.success("Plan type created successfully!");
      }

      refetchTypes();
      resetTypeForm();
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to save plan type";
      toast.error(errorMessage);
      console.error("Error saving type:", error);
    }
  };

  const handleTypeEdit = (type: PlanType) => {
    setEditingType(type);
    setTypeFormData({
      name: type.name,
      network: type.network.toString(),
      is_active: type.isActive,
    });
    setShowTypeModal(true);
  };

  const handleTypeDelete = async (typeId: number) => {
    if (!confirm("Are you sure you want to delete this plan type?")) return;

    try {
      await deleteType.mutateAsync(typeId);
      toast.success("Plan type deleted successfully!");
      refetchTypes();
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to delete plan type";
      toast.error(errorMessage);
      console.error("Error deleting type:", error);
    }
  };

  const resetTypeForm = () => {
    setTypeFormData({
      name: "",
      network: "",
      is_active: true,
    });
    setEditingType(null);
    setShowTypeModal(false);
  };

  // Loading state
  if (plansLoading || typesLoading) {
    return (
      <div className="p-4 md:p-6 space-y-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <i className="ri-loader-4-line text-4xl text-red-600 animate-spin"></i>
            <p className="mt-2 text-gray-600">Loading data plans...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
            Data Plans Management
          </h1>
          <p className="text-gray-600 mt-1">
            Manage data bundle plans and plan types
          </p>
        </div>
        <button
          onClick={() =>
            activeTab === "plans"
              ? setShowPlanModal(true)
              : setShowTypeModal(true)
          }
          className="mt-4 md:mt-0 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors whitespace-nowrap"
        >
          <i className="ri-add-fill mr-2"></i>
          {activeTab === "plans" ? "Add Data Plan" : "Add Plan Type"}
        </button>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100">
        <div className="flex border-b border-gray-200">
          <button
            onClick={() => setActiveTab("plans")}
            className={`flex-1 px-6 py-3 text-sm font-medium transition-colors ${
              activeTab === "plans"
                ? "text-red-600 border-b-2 border-red-600"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            <i className="ri-database-2-line mr-2"></i>
            Data Plans ({dataPlans.length})
          </button>
          <button
            onClick={() => setActiveTab("types")}
            className={`flex-1 px-6 py-3 text-sm font-medium transition-colors ${
              activeTab === "types"
                ? "text-red-600 border-b-2 border-red-600"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            <i className="ri-list-check mr-2"></i>
            Plan Types ({planTypes.length})
          </button>
        </div>

        {/* Data Plans Tab */}
        {activeTab === "plans" && (
          <div className="p-6 space-y-6">
            {/* Filters */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Search Plans
                </label>
                <div className="relative">
                  <i className="ri-search-line absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search by name or network"
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Filter by Network
                </label>
                <select
                  value={filterNetwork}
                  onChange={(e) => setFilterNetwork(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm"
                >
                  <option value="all">All Networks</option>
                  {networks.map((network) => (
                    <option key={network.id} value={network.name}>
                      {network.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Filter by Type
                </label>
                <select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm"
                >
                  <option value="all">All Types</option>
                  {planTypes.map((type) => (
                    <option key={type.id} value={type.id}>
                      {type.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex items-end">
                <button
                  onClick={() => {
                    setSearchTerm("");
                    setFilterNetwork("all");
                    setFilterType("all");
                  }}
                  className="w-full px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm whitespace-nowrap"
                >
                  <i className="ri-refresh-fill mr-2"></i>
                  Reset
                </button>
              </div>
            </div>

            {/* Empty State */}
            {filteredPlans.length === 0 && (
              <div className="text-center py-12">
                <i className="ri-database-2-line text-5xl text-gray-300 mb-4"></i>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  No Data Plans Found
                </h3>
                <p className="text-gray-600 mb-4">
                  {searchTerm || filterNetwork !== "all" || filterType !== "all"
                    ? "Try adjusting your filters"
                    : "Get started by adding your first data plan"}
                </p>
                {!searchTerm &&
                  filterNetwork === "all" &&
                  filterType === "all" && (
                    <button
                      onClick={() => setShowPlanModal(true)}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                    >
                      <i className="ri-add-fill mr-2"></i>
                      Add Data Plan
                    </button>
                  )}
              </div>
            )}

            {/* Data Plans Table */}
            {filteredPlans.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Plan Details
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Network
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Type
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Amount
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Commission
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredPlans.map((plan) => (
                      <tr key={plan.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {plan.name}
                            </div>
                            <div className="text-sm text-gray-500">
                              Validity: {plan.validity} days
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {plan.network}
                          </div>
                          <div className="text-sm text-gray-500">
                            {plan.providerName}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                            {plan.planTypeName}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          ₦{plan.amount.toLocaleString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {plan.commission ? `${plan.commission}%` : "N/A"}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                              plan.isActive
                                ? "bg-purple-100 text-purple-800"
                                : "bg-gray-100 text-gray-800"
                            }`}
                          >
                            {plan.isActive ? "Active" : "Inactive"}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => handlePlanEdit(plan)}
                              className="w-8 h-8 flex items-center justify-center text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            >
                              <i className="ri-edit-fill"></i>
                            </button>
                            <button
                              onClick={() => handlePlanDelete(plan.id)}
                              className="w-8 h-8 flex items-center justify-center text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            >
                              <i className="ri-delete-bin-fill"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Plan Types Tab */}
        {activeTab === "types" && (
          <div className="p-6">
            {/* Empty State */}
            {planTypes.length === 0 && (
              <div className="text-center py-12">
                <i className="ri-list-check text-5xl text-gray-300 mb-4"></i>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  No Plan Types Found
                </h3>
                <p className="text-gray-600 mb-4">
                  Get started by adding your first plan type
                </p>
                <button
                  onClick={() => setShowTypeModal(true)}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  <i className="ri-add-fill mr-2"></i>
                  Add Plan Type
                </button>
              </div>
            )}

            {/* Plan Types Grid */}
            {planTypes.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {planTypes.map((type) => (
                  <div
                    key={type.id}
                    className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-lg font-semibold text-gray-900">
                        {type.name}
                      </h3>
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                          type.isActive
                            ? "bg-purple-100 text-purple-800"
                            : "bg-gray-100 text-gray-800"
                        }`}
                      >
                        {type.isActive ? "Active" : "Inactive"}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mb-4">
                      Network: {type.networkName}
                    </p>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleTypeEdit(type)}
                        className="flex-1 px-3 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors text-sm"
                      >
                        <i className="ri-edit-fill mr-1"></i>
                        Edit
                      </button>
                      <button
                        onClick={() => handleTypeDelete(type.id)}
                        className="flex-1 px-3 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors text-sm"
                      >
                        <i className="ri-delete-bin-fill mr-1"></i>
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Data Plan Modal */}
      {showPlanModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900">
                  {editingPlan ? "Edit Data Plan" : "Add New Data Plan"}
                </h3>
                <button
                  onClick={resetPlanForm}
                  className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <i className="ri-close-fill text-xl"></i>
                </button>
              </div>

              <form onSubmit={handlePlanSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Network
                    </label>
                    <select
                      value={planFormData.network}
                      onChange={(e) =>
                        setplanFormData({
                          ...planFormData,
                          network: e.target.value,
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm"
                      required
                    >
                      <option value="">Select Network</option>
                      {networks.map((network) => (
                        <option key={network.id} value={network.id}>
                          {network.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Plan Type
                    </label>
                    <select
                      value={planFormData.plan_type}
                      onChange={(e) =>
                        setplanFormData({
                          ...planFormData,
                          plan_type: e.target.value,
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm"
                      required
                    >
                      <option value="">Select Type</option>
                      {planTypes.map((type) => (
                        <option key={type.id} value={type.id}>
                          {type.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Plan Name
                  </label>
                  <input
                    type="text"
                    value={planFormData.name}
                    onChange={(e) =>
                      setplanFormData({ ...planFormData, name: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm"
                    placeholder="e.g., 1GB SME - 30 Days"
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Plan ID
                    </label>
                    <input
                      type="number"
                      value={planFormData.plan_id}
                      onChange={(e) =>
                        setplanFormData({
                          ...planFormData,
                          plan_id: e.target.value,
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Provider
                    </label>
                    <select
                      value={planFormData.provider}
                      onChange={(e) =>
                        setplanFormData({
                          ...planFormData,
                          provider: e.target.value,
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm"
                      required
                    >
                      <option value="">Select Provider</option>
                      {providers.map((provider) => (
                        <option key={provider.id} value={provider.id}>
                          {provider.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Amount (₦)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={planFormData.amount}
                      onChange={(e) =>
                        setplanFormData({
                          ...planFormData,
                          amount: e.target.value,
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Commission (%)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={planFormData.commission}
                      onChange={(e) =>
                        setplanFormData({
                          ...planFormData,
                          commission: e.target.value,
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm"
                      placeholder="Optional"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Validity (days)
                    </label>
                    <input
                      type="number"
                      value={planFormData.validity}
                      onChange={(e) =>
                        setplanFormData({
                          ...planFormData,
                          validity: e.target.value,
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm"
                      required
                    />
                  </div>
                </div>

                <div className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    id="plan_is_active"
                    checked={planFormData.is_active}
                    onChange={(e) =>
                      setplanFormData({
                        ...planFormData,
                        is_active: e.target.checked,
                      })
                    }
                    className="w-4 h-4 text-red-600 border-gray-300 rounded focus:ring-red-500"
                  />
                  <label
                    htmlFor="plan_is_active"
                    className="text-sm font-medium text-gray-700"
                  >
                    Active Plan
                  </label>
                </div>

                <div className="flex space-x-3 pt-4">
                  <button
                    type="submit"
                    disabled={createPlan.isPending || updatePlan.isPending}
                    className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {createPlan.isPending || updatePlan.isPending ? (
                      <>
                        <i className="ri-loader-4-line animate-spin mr-2"></i>
                        {editingPlan ? "Updating..." : "Creating..."}
                      </>
                    ) : (
                      <>{editingPlan ? "Update Plan" : "Create Plan"}</>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={resetPlanForm}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Plan Type Modal */}
      {showTypeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900">
                  {editingType ? "Edit Plan Type" : "Add New Plan Type"}
                </h3>
                <button
                  onClick={resetTypeForm}
                  className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <i className="ri-close-fill text-xl"></i>
                </button>
              </div>

              <form onSubmit={handleTypeSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Type Name
                  </label>
                  <input
                    type="text"
                    value={typeFormData.name}
                    onChange={(e) =>
                      setTypeFormData({ ...typeFormData, name: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm"
                    placeholder="e.g., SME, GIFTING, CORPORATE"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Network
                  </label>
                  <select
                    value={typeFormData.network}
                    onChange={(e) =>
                      setTypeFormData({
                        ...typeFormData,
                        network: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm"
                    required
                  >
                    <option value="">Select Network</option>
                    {networks.map((network) => (
                      <option key={network.id} value={network.id}>
                        {network.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    id="type_is_active"
                    checked={typeFormData.is_active}
                    onChange={(e) =>
                      setTypeFormData({
                        ...typeFormData,
                        is_active: e.target.checked,
                      })
                    }
                    className="w-4 h-4 text-red-600 border-gray-300 rounded focus:ring-red-500"
                  />
                  <label
                    htmlFor="type_is_active"
                    className="text-sm font-medium text-gray-700"
                  >
                    Active Type
                  </label>
                </div>

                <div className="flex space-x-3 pt-4">
                  <button
                    type="submit"
                    disabled={createType.isPending || updateType.isPending}
                    className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {createType.isPending || updateType.isPending ? (
                      <>
                        <i className="ri-loader-4-line animate-spin mr-2"></i>
                        {editingType ? "Updating..." : "Creating..."}
                      </>
                    ) : (
                      <>{editingType ? "Update Type" : "Create Type"}</>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={resetTypeForm}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
